import {
  CONSUMER_WEBHOOK_DELIVERIES_LIMIT,
  CONSUMER_WEBHOOK_DELIVERY_STATUS_LABELS,
  CONSUMER_WEBHOOK_EVENT_TYPE_LABELS,
  type ConsumerWebhookDelivery,
  type ConsumerWebhookDeliveryStatus,
} from '@easyhelp/shared';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import {
  TEST_DELIVERY_POLL_MAX_MS,
  useConsumerWebhookDeliveries,
  useSendConsumerWebhookTest,
} from '../../../api/consumer-webhooks';
import type { ApiError } from '../../../api/client';
import { formatDateTime } from '../format';
import { deliveryResponseLabel, testFeedback } from './webhook-format';

export interface WebhookDeliveriesProps {
  consumerId: string;
  /** Se vero c'è «Invia evento di prova» (notifica attiva, consumer attivo). */
  canTest: boolean;
  /** Errore dell'invio della prova, perché il dialogo reagisca a una revoca appena avvenuta. */
  onTestError?: (error: ApiError) => void;
}

const STATUS_COLORS: Record<ConsumerWebhookDeliveryStatus, 'default' | 'info' | 'success' | 'error'> = {
  in_coda: 'info',
  consegnato: 'success',
  fallito: 'error',
  scartato: 'default',
};

/**
 * Esito delle ultime consegne (istante, evento, stato, risposta, tentativi) e l'evento di prova,
 * con un riscontro che segue la consegna appena inviata finché non si conclude, per al più
 * `TEST_DELIVERY_POLL_MAX_MS`; poi resta «Aggiorna». Il riscontro sta in una regione live sempre
 * presente, così i lettori di schermo annunciano ogni cambio.
 */
export function WebhookDeliveries({ consumerId, canTest, onTestError }: WebhookDeliveriesProps) {
  const sendTest = useSendConsumerWebhookTest(consumerId);
  const testId = sendTest.data?.id ?? null;
  const [expiredId, setExpiredId] = useState<string | null>(null);
  const list = useConsumerWebhookDeliveries(consumerId, testId !== expiredId ? testId : null);
  const deliveries = list.data?.deliveries ?? [];
  const test = sendTest.data && (deliveries.find((delivery) => delivery.id === sendTest.data.id) ?? sendTest.data);
  const feedback = test ? testFeedback(test, testId === expiredId) : null;

  useEffect(() => {
    if (!testId) return;
    const timer = setTimeout(() => setExpiredId(testId), TEST_DELIVERY_POLL_MAX_MS);
    return () => clearTimeout(timer);
  }, [testId]);

  const send = () => {
    if (sendTest.isPending) return;
    sendTest.mutate(undefined, { onError: onTestError });
  };

  return (
    <Stack component="section" spacing={2} aria-labelledby="webhook-deliveries-title">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
      >
        <Typography id="webhook-deliveries-title" variant="subtitle1" component="h3" fontWeight={600}>
          Ultime consegne
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => void list.refetch()}>
            Aggiorna
          </Button>
          {canTest && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SendOutlinedIcon />}
              onClick={send}
              aria-disabled={sendTest.isPending}
            >
              Invia evento di prova
            </Button>
          )}
        </Stack>
      </Stack>

      <Box role="status" aria-label="Esito dell’evento di prova">
        {feedback && (
          <Alert severity={feedback.severity} role="none">
            {feedback.message}
          </Alert>
        )}
      </Box>
      {sendTest.isError && <Alert severity="error">Evento di prova non inviato: {sendTest.error.message}</Alert>}

      {list.isPending && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Caricamento delle consegne…</Typography>
        </Stack>
      )}
      {list.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void list.refetch()}>
              Riprova
            </Button>
          }
        >
          Consegne non disponibili: {list.error.message}
        </Alert>
      )}
      {list.data &&
        (deliveries.length === 0 ? (
          <Typography color="text.secondary">
            Nessuna consegna: gli eventi compaiono qui quando un ticket cambia stato o invii un evento di prova.
          </Typography>
        ) : (
          <DeliveriesTable deliveries={deliveries} />
        ))}
    </Stack>
  );
}

function DeliveriesTable({ deliveries }: { deliveries: ConsumerWebhookDelivery[] }) {
  return (
    <TableContainer>
      <Table size="small" aria-label={`Ultime ${CONSUMER_WEBHOOK_DELIVERIES_LIMIT} consegne`}>
        <TableHead>
          <TableRow>
            <TableCell>Istante</TableCell>
            <TableCell>Evento</TableCell>
            <TableCell>Stato</TableCell>
            <TableCell>Risposta</TableCell>
            <TableCell>Tentativi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deliveries.map((delivery) => (
            <TableRow key={delivery.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(delivery.createdAt)}</TableCell>
              <TableCell>
                {CONSUMER_WEBHOOK_EVENT_TYPE_LABELS[delivery.type]}
                {delivery.ticketCode && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}
                    · {delivery.ticketCode}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Stack spacing={0.5} alignItems="flex-start">
                  <Chip
                    size="small"
                    label={CONSUMER_WEBHOOK_DELIVERY_STATUS_LABELS[delivery.status]}
                    color={STATUS_COLORS[delivery.status]}
                    variant={delivery.status === 'scartato' ? 'outlined' : 'filled'}
                  />
                  {delivery.nextAttemptAt && delivery.attempts > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Prossimo tentativo {formatDateTime(delivery.nextAttemptAt)}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>{deliveryResponseLabel(delivery)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {delivery.attempts} di {delivery.maxAttempts}
                {delivery.lastAttemptAt && (
                  <Typography component="div" variant="caption" color="text.secondary">
                    ultimo {formatDateTime(delivery.lastAttemptAt)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
